import React, { useState, useEffect, useRef } from 'react';
import AppleView from '../components/AppleView';
import EngineView from '../components/EngineView';
import PipelineView from '../components/PipelineView';
import PerformanceTab from '../components/PerformanceTab';

interface PieceDetailProps {
  brand: string;
  piece: any;
  engineMode: boolean;
  onBack: () => void;
  onAction: (action: string, slug: string) => void;
  onToggleEngine: () => void;
  /** Override API base path for chapters: /api/{brand}/volumes/{vol}/chapters/{ch} */
  apiBase?: string;
}

function humanTitle(slug: string): string {
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export default function PieceDetail({ brand, piece, engineMode, onBack, onAction, onToggleEngine, apiBase }: PieceDetailProps) {
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [performanceMode, setPerformanceMode] = useState(false);

  // Compute API base: either custom (for chapters) or default (for pieces)
  const detailUrl = apiBase || `/api/${brand}/pieces/${piece.slug}`;

  // Fetch enriched detail (configured formats, routing, validation)
  // #15 fix: track loading state to prevent layout shift
  useEffect(() => {
    setDetail(null);
    setLoading(true);
    fetch(detailUrl)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => { setDetail(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [piece.slug, brand, detailUrl]);

  // Reset performance mode when navigating to a different piece
  useEffect(() => { setPerformanceMode(false); }, [piece.slug]);

  // Re-fetch detail silently when pipeline finishes so configuredFormats/renditions are fresh
  const prevPipelineStatus = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevPipelineStatus.current;
    prevPipelineStatus.current = piece.pipelineStatus;
    if (prev === 'running' && piece.pipelineStatus !== 'running') {
      fetch(detailUrl)
        .then(r => r.json())
        .then((d) => setDetail(d))
        .catch(() => {});
    }
  }, [piece.pipelineStatus, detailUrl]);

  const isRunning = piece.pipelineStatus === 'running';

  return (
    <div className="piece-detail">
      <div className="piece-detail__inner">
        {/* Top bar: back + Social Studio CTA + engine toggle + performance toggle */}
        <div className="piece-detail__topbar">
          <button className="piece-detail__back" onClick={onBack}>{'\u2190'} Library</button>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              className="btn btn-secondary"
              data-testid="piece-detail-create-social"
              onClick={() => onAction('social-studio', piece.slug)}
              title="Generate a LinkedIn or X post from this piece's narrative"
              style={{ fontSize: 12, padding: '6px 12px' }}
            >
              Create social post
            </button>
            <button
              className={`piece-detail__engine-toggle ${engineMode ? 'piece-detail__engine-toggle--on' : ''}`}
              onClick={() => { onToggleEngine(); setPerformanceMode(false); }}
              title="Show pipeline internals: lineage, fan-out, quality gates (Cmd+Shift+E)"
            >
              {engineMode ? '\u2699 Engine View' : '\u2699 Show Engine'} <kbd className="piece-detail__engine-kbd">{'\u21E7\u2318'}E</kbd>
            </button>
            <button
              className={`piece-detail__engine-toggle ${performanceMode ? 'piece-detail__engine-toggle--on' : ''}`}
              onClick={() => { setPerformanceMode(m => !m); if (engineMode) onToggleEngine(); }}
              title="Platform stats, SEO metrics, AI citation tracking"
            >
              {'\uD83D\uDCCA'} Performance
            </button>
          </div>
        </div>

        {/* Loading state — #15 fix */}
        {loading && !isRunning ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, color: 'var(--text-tertiary)' }}>
            Loading...
          </div>
        ) : isRunning ? (
          <PipelineView
            slug={piece.slug}
            stages={piece.stages || {}}
            runningStage={piece.runningStage}
            completedStages={piece.completedStages}
            failedStage={piece.failedStage}
          />
        ) : performanceMode ? (
          <PerformanceTab brand={brand} slug={piece.slug} />
        ) : engineMode ? (
          <EngineView
            brand={brand}
            piece={piece}
            detail={detail}
            onAction={onAction}
            onToggleEngine={onToggleEngine}
            apiBase={apiBase}
          />
        ) : (
          <AppleView
            key={piece.slug}
            brand={brand}
            piece={piece}
            detail={detail}
            onAction={onAction}
            onToggleEngine={onToggleEngine}
            apiBase={apiBase}
          />
        )}
      </div>
    </div>
  );
}
